import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  DEFAULT_PARTNER_REVENUE_PRESET,
  formatPartnerRevenueDateParam,
  getPartnerRevenuePresetRange,
  isPartnerRevenuePresetId,
  parsePartnerRevenueDateParam,
  type PartnerRevenueDateRange,
  type PartnerRevenuePeriodId,
  type PartnerRevenuePresetId,
} from "../utils/partnerRevenuePeriodPresets";

/**
 * Task 0038 (owner, 2026-09-30): one period model, shared by every partner
 * orders/earnings page, persisted in the URL — shareable, survives reload,
 * and (since it's the URL) never leaks between pages. `paramPrefix` lets a
 * page host more than one independent selector if it ever needs to; every
 * current consumer just uses the default.
 */
export function usePartnerRevenuePeriod(paramPrefix = "period") {
  const [searchParams, setSearchParams] = useSearchParams();

  const presetParam = searchParams.get(paramPrefix);
  const fromParam = searchParams.get(`${paramPrefix}From`);
  const toParam = searchParams.get(`${paramPrefix}To`);

  let periodId: PartnerRevenuePeriodId;
  if (presetParam === "custom") {
    periodId = "custom";
  } else if (isPartnerRevenuePresetId(presetParam)) {
    periodId = presetParam;
  } else {
    periodId = DEFAULT_PARTNER_REVENUE_PRESET;
  }

  const range: PartnerRevenueDateRange = useMemo(() => {
    if (periodId === "custom") {
      const from = parsePartnerRevenueDateParam(fromParam);
      const to = parsePartnerRevenueDateParam(toParam);
      if (from && to) return { from, to };
      // Malformed/missing custom range in the URL — fall back to the
      // default rather than throwing or querying with garbage dates.
      return getPartnerRevenuePresetRange(DEFAULT_PARTNER_REVENUE_PRESET);
    }
    return getPartnerRevenuePresetRange(periodId);
  }, [periodId, fromParam, toParam]);

  const setPreset = useCallback(
    (preset: PartnerRevenuePresetId) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(paramPrefix, preset);
          next.delete(`${paramPrefix}From`);
          next.delete(`${paramPrefix}To`);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, paramPrefix],
  );

  const setCustomRange = useCallback(
    (customRange: PartnerRevenueDateRange) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(paramPrefix, "custom");
          next.set(
            `${paramPrefix}From`,
            formatPartnerRevenueDateParam(customRange.from),
          );
          next.set(
            `${paramPrefix}To`,
            formatPartnerRevenueDateParam(customRange.to),
          );
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, paramPrefix],
  );

  return { periodId, range, setPreset, setCustomRange };
}
